import { Chart, registerables } from "chart.js";
import { fm } from "./analytics";

Chart.register(...registerables);

// Module-level registry of chart instances keyed by canvas id
const registry = {};

export function destroyChart(id) {
  if (registry[id]) {
    try { registry[id].destroy(); } catch (e) { console.warn(`[chartFactory] destroy("${id}") failed:`, e?.message || e); }
    delete registry[id];
  }
}

export function destroyAll() {
  Object.keys(registry).forEach(destroyChart);
}

// Detect compact viewport (mobile/tablet portrait) for adaptive sizing
const isCompact = () =>
  typeof window !== "undefined" && window.innerWidth < 700;

export function mkC(id, tp, labs, ds, ex = {}) {
  destroyChart(id);
  const el = typeof document !== "undefined" ? document.getElementById(id) : null;
  if (!el) return;
  const cx = el.getContext("2d");
  const ip = tp === "doughnut" || tp === "pie";
  const compact = isCompact();
  const fSize = compact ? 11 : 10;

  const opts = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
    interaction: ip
      ? { mode: "nearest", intersect: true }
      : { mode: "index", intersect: false, axis: "x" },
    animation: { duration: 700, easing: "easeOutQuart" },
    transitions: {
      active: { animation: { duration: 250 } },
      resize: { animation: { duration: 0 } },
    },
    onHover: (event, elements) => {
      if (event?.native?.target?.style) {
        event.native.target.style.cursor = elements.length ? "pointer" : "default";
      }
    },
    plugins: {
      legend: {
        display: ip || ds.length > 1,
        position: ip ? (compact ? "bottom" : "right") : "top",
        align: "center",
        labels: {
          font: { family: "Cairo", size: fSize },
          color: "#6B4E3D",
          boxWidth: 12,
          padding: compact ? 10 : 8,
          usePointStyle: true,
          pointStyle: "rectRounded",
        },
        onHover: (e) => {
          if (e?.native?.target?.style) e.native.target.style.cursor = "pointer";
        },
        onLeave: (e) => {
          if (e?.native?.target?.style) e.native.target.style.cursor = "default";
        },
      },
      tooltip: {
        rtl: true,
        enabled: true,
        mode: ip ? "nearest" : "index",
        intersect: ip,
        position: "nearest",
        titleFont: { family: "El Messiri", size: 13, weight: "600" },
        bodyFont: { family: "Cairo", size: 12 },
        footerFont: { family: "Outfit", size: 11, weight: "600" },
        padding: 12,
        cornerRadius: 12,
        boxPadding: 6,
        backgroundColor: "rgba(58, 43, 35, 0.96)",
        titleColor: "#C2A97F",
        bodyColor: "#F3EBDD",
        footerColor: "#D5C19E",
        borderColor: "rgba(194, 169, 127, 0.4)",
        borderWidth: 1,
        caretSize: 7,
        displayColors: true,
        usePointStyle: true,
        callbacks: {
          label: function (ctx) {
            const raw = ctx.parsed?.y ?? ctx.parsed;
            // Defensive coercion: parsed may be an object {x,y}, a [low,high] array
            // (floating bar), or any other shape depending on dataset config.
            let v = raw;
            if (Array.isArray(v)) v = Number(v[1]) - Number(v[0]);
            else if (v && typeof v === "object") v = Number(v.y ?? ctx.raw?.y ?? ctx.raw ?? 0);
            if (typeof v !== "number" || Number.isNaN(v)) v = 0;
            if (ip) {
              const t = ctx.dataset.data.reduce((a, b) => a + (Number(b) || 0), 0);
              return " " + ctx.label + ": " + v + " (" + (t ? (v / t * 100).toFixed(1) : 0) + "%)";
            }
            const lbl = ctx.dataset.label || "";
            if (lbl.includes("%") || lbl.includes("تحويل") || lbl.includes("هدف") || lbl.includes("هامش")) {
              return " " + lbl + ": " + v.toFixed(1) + "%";
            }
            if (Math.abs(v) >= 100 && !lbl.includes("فاتورة") && !lbl.includes("عدد") && !lbl.includes("كمية")) {
              return " " + lbl + ": " + fm(v) + " ر";
            }
            return " " + lbl + ": " + v;
          },
          // Footer for line/bar charts: sum of all datasets at this index
          footer: function (items) {
            if (ip || items.length < 2) return undefined;
            const hasPct = items.some((it) => {
              const lbl = it.dataset.label || "";
              return lbl.includes("%") || lbl.includes("هدف");
            });
            if (hasPct) return undefined;
            const total = items.reduce((s, it) => {
              const raw = it.parsed?.y ?? it.parsed;
              let val = raw;
              if (Array.isArray(val)) val = Number(val[1]) - Number(val[0]);
              else if (val && typeof val === "object") val = Number(val.y ?? it.raw?.y ?? it.raw ?? 0);
              return s + (Number(val) || 0);
            }, 0);
            return "المجموع: " + fm(total) + " ر";
          },
        },
      },
    },
    scales: !ip
      ? {
          x: {
            ticks: {
              font: { family: "Cairo", size: compact ? 10 : 9 },
              color: "#8C7161",
              maxRotation: compact ? 0 : 45,
              autoSkip: true,
              autoSkipPadding: 8,
            },
            grid: { display: false },
          },
          y: {
            ticks: {
              font: { family: "Outfit", size: compact ? 10 : 9 },
              color: "#8C7161",
              callback: (v) => (Math.abs(v) >= 1000 ? Math.round(v / 1000) + "k" : v),
            },
            grid: { color: "#EAE3D5", drawTicks: false, borderDash: [4, 4] },
            border: { display: false },
          },
        }
      : {},
  };

  // Deep-merge user-provided overrides
  if (ex.plugins) {
    opts.plugins = { ...opts.plugins, ...ex.plugins };
    if (ex.plugins.legend) opts.plugins.legend = { ...opts.plugins.legend, ...ex.plugins.legend };
    if (ex.plugins.tooltip) opts.plugins.tooltip = { ...opts.plugins.tooltip, ...ex.plugins.tooltip };
  }
  if (ex.scales) {
    opts.scales = { ...(opts.scales || {}), ...ex.scales };
  }
  // Pass through other top-level overrides
  Object.keys(ex).forEach((k) => {
    if (k !== "plugins" && k !== "scales") opts[k] = ex[k];
  });

  // Apply rounded bars and softened lines for nicer visuals across all charts
  const enhancedDs = ds.map((d) => {
    const base = { ...d };
    if (tp === "bar" && base.borderRadius === undefined) base.borderRadius = 6;
    if (tp === "bar" && base.maxBarThickness === undefined) base.maxBarThickness = 38;
    if (tp === "line") {
      if (base.tension === undefined) base.tension = 0.35;
      if (base.borderWidth === undefined) base.borderWidth = 2.5;
      if (base.pointHoverRadius === undefined) base.pointHoverRadius = 6;
      if (base.pointHoverBorderWidth === undefined) base.pointHoverBorderWidth = 2;
    }
    return base;
  });

  registry[id] = new Chart(cx, { type: tp, data: { labels: labs, datasets: enhancedDs }, options: opts });
  return registry[id];
}

// Resize all charts on viewport change for true responsiveness
if (typeof window !== "undefined") {
  let resizeTimer = null;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      Object.values(registry).forEach((c) => {
        try { c.resize(); } catch (e) { console.warn("[chartFactory] resize failed:", e?.message || e); }
      });
    }, 120);
  });
}
